import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import {
  isSupported, randomSaltB64, deriveKey, encryptJSON, decryptJSON, CURRENT_KDF,
  makeVerifier, checkVerifier,
  generateMasterKey, generateRecoveryKey, deriveRecoveryKek,
  wrapMasterKey, unwrapMasterKey,
} from '../lib/vaultCrypto';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTO_LOCK_MS = 15 * 60 * 1000; // lock after 15 min of inactivity

const VaultContext = createContext();

export const useVault = () => {
  const ctx = useContext(VaultContext);
  if (!ctx) throw new Error('useVault must be used within VaultProvider');
  return ctx;
};

export const VaultProvider = ({ children }) => {
  const { user } = useAuth();
  // 'loading' | 'uninitialized' | 'locked' | 'unlocked'
  const [status, setStatus] = useState('loading');
  const [scheme, setScheme] = useState(2);      // 1 = legacy, 2 = envelope
  const [notes, setNotes] = useState([]);
  const [busy, setBusy] = useState(false);
  const keyRef = useRef(null);          // note-encryption key (master) — memory only
  const metaRef = useRef(null);         // { salt, wrapped_pass, ... }
  const lockTimerRef = useRef(null);

  const lock = useCallback(() => {
    keyRef.current = null;
    setNotes([]);
    setStatus((s) => (s === 'uninitialized' ? s : 'locked'));
  }, []);

  // --- auto-lock on inactivity -------------------------------------------
  const touch = useCallback(() => {
    if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
    if (!keyRef.current) return;
    lockTimerRef.current = setTimeout(lock, AUTO_LOCK_MS);
  }, [lock]);

  useEffect(() => {
    if (status !== 'unlocked') return undefined;
    const events = ['mousedown', 'keydown', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, touch));
    touch();
    return () => {
      events.forEach((e) => window.removeEventListener(e, touch));
      if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
    };
  }, [status, touch]);

  // Drop the key the moment the member signs out.
  useEffect(() => {
    if (!user || user === false) {
      keyRef.current = null;
      metaRef.current = null;
      setNotes([]);
      setStatus('loading');
    }
  }, [user]);

  const refreshStatus = useCallback(async () => {
    if (!user || user === false) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/vault/status`, { withCredentials: true });
      if (!data.initialized) {
        metaRef.current = null;
        setStatus('uninitialized');
      } else {
        metaRef.current = data;
        setScheme(data.scheme || 1);
        setStatus(keyRef.current ? 'unlocked' : 'locked');
      }
    } catch (e) {
      setStatus('locked');
    }
  }, [user]);

  const decryptNotes = useCallback(async (key, rows) => {
    const out = [];
    for (const row of rows) {
      try {
        const value = await decryptJSON(key, row.iv, row.ciphertext);
        out.push({ id: row.id, ...value, created_at: row.created_at, updated_at: row.updated_at });
      } catch (e) {
        out.push({ id: row.id, title: '[Could not decrypt]', body: '', broken: true,
                   created_at: row.created_at, updated_at: row.updated_at });
      }
    }
    return out;
  }, []);

  const fetchAndDecrypt = useCallback(async (key) => {
    const { data } = await axios.get(`${API_URL}/api/vault/notes`, { withCredentials: true });
    setNotes(await decryptNotes(key, data));
  }, [decryptNotes]);

  const loadNotes = useCallback(async () => {
    if (!keyRef.current) return;
    await fetchAndDecrypt(keyRef.current);
  }, [fetchAndDecrypt]);

  /** Create a brand-new envelope vault. Returns the recovery key ONCE. */
  const createVault = useCallback(async (passphrase) => {
    if (!isSupported()) return { ok: false, error: 'This browser cannot use the vault (needs HTTPS)' };
    setBusy(true);
    try {
      const salt = randomSaltB64();
      const master = await generateMasterKey();
      // A new vault is stamped with today's parameters, and unlocking will
      // read them back rather than assume them.
      const passKek = await deriveKey(passphrase, salt, CURRENT_KDF);
      const recoveryKey = generateRecoveryKey();
      const recoveryKek = await deriveRecoveryKek(recoveryKey, salt, CURRENT_KDF);

      const wrapped_pass = await wrapMasterKey(master, passKek);
      const wrapped_recovery = await wrapMasterKey(master, recoveryKek);

      await axios.post(`${API_URL}/api/vault/init`,
        { salt, kdf: CURRENT_KDF, wrapped_pass, wrapped_recovery }, { withCredentials: true });

      keyRef.current = master;
      metaRef.current = { salt, kdf: CURRENT_KDF, wrapped_pass, scheme: 2 };
      setScheme(2);
      setNotes([]);
      setStatus('unlocked');
      return { ok: true, recoveryKey };
    } catch (e) {
      return { ok: false, error: e.response?.data?.detail || 'Could not create the vault' };
    } finally {
      setBusy(false);
    }
  }, []);

  /** Unlock with the passphrase. Handles both the legacy and envelope schemes. */
  const unlock = useCallback(async (passphrase) => {
    const meta = metaRef.current;
    if (!meta) return { ok: false, error: 'Vault not ready' };
    setBusy(true);
    try {
      // Whatever this vault was built with, not whatever this build prefers.
      const derived = await deriveKey(passphrase, meta.salt, meta.kdf);
      let noteKey;

      if (meta.scheme === 2 || meta.wrapped_pass) {
        try {
          noteKey = await unwrapMasterKey(meta.wrapped_pass, derived);
        } catch (e) {
          return { ok: false, error: 'Wrong passphrase' };
        }
      } else {
        // Legacy: the derived key IS the note key, checked against the verifier.
        const ok = await checkVerifier(derived, meta.verifier_iv, meta.verifier_ciphertext);
        if (!ok) return { ok: false, error: 'Wrong passphrase' };
        noteKey = derived;
      }

      keyRef.current = noteKey;
      setStatus('unlocked');
      await fetchAndDecrypt(noteKey);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: 'Could not unlock the vault' };
    } finally {
      setBusy(false);
    }
  }, [fetchAndDecrypt]);

  /**
   * Upgrade a legacy vault: re-encrypt every note under a fresh master key and
   * store both wraps. Sent as ONE request so a vault is never half-rewrapped.
   * Requires the vault to be unlocked. Returns the new recovery key.
   */
  const upgradeVault = useCallback(async (passphrase) => {
    if (!keyRef.current) return { ok: false, error: 'Unlock the vault first' };
    if (!passphrase) return { ok: false, error: 'Confirm your passphrase to upgrade' };
    setBusy(true);
    try {
      const { data: rows } = await axios.get(`${API_URL}/api/vault/notes`, { withCredentials: true });
      const plain = await decryptNotes(keyRef.current, rows);
      if (plain.some((n) => n.broken)) {
        return { ok: false, error: 'Some notes could not be read — upgrade aborted so nothing is lost' };
      }

      const salt = randomSaltB64();
      const master = await generateMasterKey();
      const recoveryKey = generateRecoveryKey();
      const passKek = await deriveKey(passphrase, salt, CURRENT_KDF);
      const recoveryKek = await deriveRecoveryKek(recoveryKey, salt, CURRENT_KDF);

      const reNotes = [];
      for (const n of plain) {
        const { iv, ciphertext } = await encryptJSON(master, { title: n.title, body: n.body });
        reNotes.push({ id: n.id, iv, ciphertext });
      }

      await axios.post(`${API_URL}/api/vault/rekey`, {
        salt,
        kdf: CURRENT_KDF,
        wrapped_pass: await wrapMasterKey(master, passKek),
        wrapped_recovery: await wrapMasterKey(master, recoveryKek),
        notes: reNotes,
      }, { withCredentials: true });

      keyRef.current = master;
      metaRef.current = { salt, kdf: CURRENT_KDF, scheme: 2 };
      setScheme(2);
      await fetchAndDecrypt(master);
      return { ok: true, recoveryKey };
    } catch (e) {
      return { ok: false, error: e.response?.data?.detail || 'Upgrade failed — nothing was changed' };
    } finally {
      setBusy(false);
    }
  }, [decryptNotes, fetchAndDecrypt]);

  /** Change the passphrase. The recovery key keeps working (salt unchanged). */
  const changePassphrase = useCallback(async (newPassphrase) => {
    if (!keyRef.current || !metaRef.current) return { ok: false, error: 'Unlock the vault first' };
    setBusy(true);
    try {
      const salt = metaRef.current.salt;
      // Deliberately the EXISTING parameters, not CURRENT_KDF. This path keeps
      // the recovery wrapping untouched, and that wrapping was sealed with a
      // key derived at the old cost -- raising it here would silently make the
      // member's written-down recovery key stop working. Parameters move only
      // where both wrappings are rewritten together.
      const kdf = metaRef.current.kdf;
      const passKek = await deriveKey(newPassphrase, salt, kdf);
      const wrapped_pass = await wrapMasterKey(keyRef.current, passKek);
      await axios.post(`${API_URL}/api/vault/rekey`, { salt, kdf, wrapped_pass, notes: [] },
        { withCredentials: true });
      metaRef.current = { ...metaRef.current, wrapped_pass };
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.response?.data?.detail || 'Could not change the passphrase' };
    } finally {
      setBusy(false);
    }
  }, []);

  /** Unlock using the written-down recovery key (when the passphrase is lost). */
  const recoverWithKey = useCallback(async (recoveryKey) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/vault/recovery-blob`, {}, { withCredentials: true });
      const kek = await deriveRecoveryKek(recoveryKey, data.salt, data.kdf);
      let master;
      try {
        master = await unwrapMasterKey(data.wrapped_recovery, kek);
      } catch (e) {
        return { ok: false, error: 'That recovery key does not match this vault' };
      }
      keyRef.current = master;
      metaRef.current = { ...(metaRef.current || {}), salt: data.salt, kdf: data.kdf, scheme: 2 };
      setScheme(2);
      setStatus('unlocked');
      await fetchAndDecrypt(master);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.response?.data?.detail || 'Recovery is not available for this vault' };
    } finally {
      setBusy(false);
    }
  }, [fetchAndDecrypt]);

  const saveNote = useCallback(async ({ id, title, body }) => {
    if (!keyRef.current) return { ok: false, error: 'Vault is locked' };
    const payload = await encryptJSON(keyRef.current, { title, body });
    if (id) {
      await axios.patch(`${API_URL}/api/vault/notes/${id}`, payload, { withCredentials: true });
    } else {
      await axios.post(`${API_URL}/api/vault/notes`, payload, { withCredentials: true });
    }
    await loadNotes();
    return { ok: true };
  }, [loadNotes]);

  const deleteNote = useCallback(async (id) => {
    await axios.delete(`${API_URL}/api/vault/notes/${id}`, { withCredentials: true });
    await loadNotes();
  }, [loadNotes]);

  const resetVault = useCallback(async () => {
    await axios.post(`${API_URL}/api/vault/reset`, {}, { withCredentials: true });
    keyRef.current = null;
    metaRef.current = null;
    setNotes([]);
    setStatus('uninitialized');
  }, []);

  const value = {
    status, scheme, notes, busy, supported: isSupported(),
    refreshStatus, createVault, unlock, lock, loadNotes,
    upgradeVault, changePassphrase, recoverWithKey,
    saveNote, deleteNote, resetVault,
  };

  return <VaultContext.Provider value={value}>{children}</VaultContext.Provider>;
};
